// The env-var manifest contract every surface in the workspace writes to.
//
// A surface — a Worker, a Convex deployment, the retells Vite build — owns a
// pure zod object schema (no `process.env`, no `import.meta.env`, no runtime
// globals) whose fields are wrapped in `envField` so each one says where it
// lives and what it is for. That one object is then three things at once: the
// runtime validator, the source `infra/scripts/env-example.ts` renders
// `.env.example` / `.dev.vars.example` from, and the list
// `infra/scripts/env-check.ts` verifies a deployment against. The prose
// tables it replaces (deploy-playbook.md, the app READMEs, `REQUIRED` arrays
// in worker/config.ts) drifted from the code because nothing read them.
//
// A REGISTRY, not `.meta()` on the global one: `.meta()` types against zod's
// `GlobalMeta`, which every package would have to augment identically, and a
// mismatch between two augmentations is a type error in whichever file the
// compiler visits last. A typed registry declares the shape once, here.
//
// Namespace import, not `import { z }` — zod ships CJS for the node condition
// and the named form resolves to `undefined` under vitest's multi-project run
// (see corpus/selection.ts).
import * as z from "zod";

/**
 * Where a variable lives, which decides how it is set and which generated
 * file lists it.
 *
 * - `secret`: `wrangler secret put` on a Worker, `convex env set` on a
 *   deployment, `.env.local` for a build — never in a committed file.
 * - `var`: a plain-text `vars` entry in wrangler.jsonc, checked per scope.
 * - `build`: a Vite `VITE_*` value read at build time from `.env.local`.
 * - `injected`: set by `infra/scripts/deploy.ts` at build time, never by an
 *   operator — skipped by the generator and by the local check.
 */
export type EnvKind = "secret" | "var" | "build" | "injected";

export interface EnvFieldMeta {
  kind: EnvKind;
  /** What the value is and what happens without it. Rendered as the comment above the line. */
  doc: string;
  /**
   * The `.env.example` value. Must never look real — a plausible placeholder
   * is worse than an obvious one (the rule apps/retells/.env.example states).
   */
  placeholder?: string;
  /** The uncommented value `.dev.vars.example` carries for local `wrangler dev`. */
  dev?: string;
  /** Present on a production deployment is a misconfiguration. */
  prodMustBeUnset?: boolean;
  /**
   * A doc that explains how to obtain the value, appended to the runtime
   * error as `Setup: <path>`.
   *
   * For the variables where knowing the command is not enough: `bunx convex
   * env set GOOGLE_ADS_CUSTOMER_ID <value>` tells an operator nothing about
   * where a customer id comes from. The hand-rolled throws these readers
   * replaced carried exactly this pointer, and dropping it would have made
   * the errors shorter and less useful.
   */
  setupDoc?: string;
}

export const envRegistry = z.registry<EnvFieldMeta>();

/**
 * Attach manifest metadata to a field. Call it on the FINISHED field — after
 * `.optional()` / `.default()` — because the registry is keyed by schema
 * instance and each of those wrappers is a new one; metadata registered on
 * the inner schema is invisible from the outer.
 */
export function envField<T extends z.ZodType>(
  schema: T,
  meta: EnvFieldMeta,
): T {
  // Registered through the widened type: `register`'s parameter is a
  // conditional over the schema's own type, which the compiler cannot decide
  // for an unresolved `T`. Same instance either way — a registry is a Map.
  (schema as z.ZodType).register(envRegistry, meta);
  return schema;
}

export type EnvSchema = z.ZodObject<Record<string, z.ZodType>>;

/** One field of a surface's schema, flattened for the generator and the checker. */
export interface EnvVarSpec extends EnvFieldMeta {
  name: string;
  /** Absent is allowed, not a fault. Derived: the field accepts `undefined`. */
  optional: boolean;
  /**
   * Absent means the field's own default takes effect — which is NOT the same
   * answer as `optional` alone gives. Both `.optional()` and `.default(x)`
   * accept `undefined`, so `optional` cannot tell them apart, and reporting a
   * defaulted name as "that feature is off" says the opposite of what is
   * happening: the feature is on, at a value nobody chose. `apps/auth`'s
   * `ALLOWED_READER_REDIRECT_DOMAINS` is the case that matters — its default
   * is PRODUCTION's allowlist, so a scope that omits it is permissive about
   * the wrong domains rather than quiet.
   *
   * Derived like `optional` is, for the same reason: parsing `undefined`
   * yields a value only when a default supplied one. Nothing is declared by
   * hand, so nothing can disagree with the field.
   */
  hasDefault: boolean;
}

/** Every field of `schema`, in declaration order. Throws on a field `envField` never saw. */
export function specsOf(schema: EnvSchema): EnvVarSpec[] {
  return Object.entries(schema.shape).map(([name, field]) => {
    const meta = envRegistry.get(field);
    if (meta === undefined) {
      throw new Error(
        `${name} carries no env metadata — wrap the field in envField() so the manifest knows where it lives`,
      );
    }
    const absent = field.safeParse(undefined);
    return {
      name,
      optional: absent.success,
      hasDefault: absent.success && absent.data !== undefined,
      ...meta,
    };
  });
}

export interface EnvDiff {
  missingRequired: string[];
  missingOptional: string[];
}

/**
 * Which declared names a set of present ones fails to provide, split by
 * whether their absence is a fault.
 *
 * Takes a plain set rather than a schema so it can answer for surfaces whose
 * present names come from somewhere no schema describes — a `.env.local`, a
 * `wrangler secret list`, one `convex env get` per name.
 */
export function diffAgainst(
  specs: readonly EnvVarSpec[],
  present: ReadonlySet<string>,
): EnvDiff {
  const missingRequired: string[] = [];
  const missingOptional: string[] = [];
  for (const spec of specs) {
    if (present.has(spec.name)) continue;
    (spec.optional ? missingOptional : missingRequired).push(spec.name);
  }
  return { missingRequired, missingOptional };
}

/**
 * A reader over a raw name→value record, one key at a time, typed by the
 * schema. Point-of-use reads are deliberate: a Convex deployment has no boot
 * to validate everything at, and a deployment that has never configured a
 * feature should stay quiet until that feature is asked for.
 *
 * An empty string counts as unset — `X=` is someone deferring the decision,
 * not making one — and a missing or invalid value throws with `hint(key)`,
 * the command that would set it, so the error names the fix.
 */
export function envReader<S extends EnvSchema>(
  schema: S,
  source: () => Readonly<Record<string, string | undefined>>,
  hint: (key: string) => string,
): <K extends keyof S["shape"] & string>(key: K) => z.output<S["shape"][K]> {
  return (key) => {
    const field = schema.shape[key];
    if (field === undefined) throw new Error(`${key} is not in the env schema`);
    const raw = source()[key];
    const result = field.safeParse(raw === "" ? undefined : raw);
    if (result.success) return result.data as z.output<S["shape"][typeof key]>;
    const reason =
      raw === undefined || raw === ""
        ? "is not set"
        : `is invalid (${result.error.issues.map((i) => i.message).join("; ")})`;
    const setupDoc = envRegistry.get(field)?.setupDoc;
    const setup = setupDoc === undefined ? "" : ` Setup: ${setupDoc}`;
    throw new Error(`${key} ${reason} — ${hint(key)}.${setup}`);
  };
}

export interface ViteEnvOptions<Name extends string> {
  /** Fields read raw elsewhere, left out so a bad value there never stops the boot. */
  readonly omit?: readonly Name[];
  /** The fix for an unset field, appended after "is not set — ". */
  readonly hint?: (key: string) => string;
}

/**
 * A Vite build's `import.meta.env`, validated as a whole at boot, where a
 * Convex deployment reads per key through {@link envReader}: the build's env
 * is fixed for its life, so one fail-fast parse is the earliest the mistake
 * can be named. Empty strings read as absent, as in `envReader`, so a
 * `.default` still applies to `X=`; the object parse and the per-field
 * explanation read the same normalized source so they cannot disagree.
 */
export function readViteEnv<
  Shape extends z.ZodRawShape,
  Omitted extends keyof Shape & string = never,
>(
  schema: z.ZodObject<Shape>,
  raw: Readonly<Record<string, unknown>>,
  { omit = [], hint }: ViteEnvOptions<Omitted> = {},
): z.output<z.ZodObject<Omit<Shape, Omitted>>> {
  const omitted = new Set<string>(omit);
  const fields = Object.entries(schema.shape).filter(
    ([name]) => !omitted.has(name),
  );
  const boot = z.object(Object.fromEntries(fields));
  const source: Record<string, unknown> = {};
  for (const [name] of fields) {
    const value = raw[name];
    if (value !== undefined && value !== "") source[name] = value;
  }
  const result = boot.safeParse(source);
  if (result.success) {
    return result.data as z.output<z.ZodObject<Omit<Shape, Omitted>>>;
  }
  const problems = fields.flatMap(([name, field]) => {
    const parsed = z.safeParse(field, source[name]);
    if (parsed.success) return [];
    if (source[name] === undefined) {
      return [
        hint === undefined
          ? `${name} is not set`
          : `${name} is not set — ${hint(name)}`,
      ];
    }
    const issues = parsed.error.issues.map((issue) => issue.message);
    return [`${name} is invalid (${issues.join("; ")})`];
  });
  // Never an empty Error: a blank message is the failure this exists to prevent.
  throw new Error(
    problems.length === 0 ? result.error.message : problems.join("; "),
  );
}

/**
 * Every field of `schema` that `env` fails, once each, in schema order. Never
 * throws: for a caller that logs a gap rather than refusing on it.
 */
export function missingBindingsOf(schema: z.ZodType, env: unknown): string[] {
  const parsed = schema.safeParse(env);
  return parsed.success ? [] : fieldsOf(parsed.error);
}

function fieldsOf(error: z.ZodError): string[] {
  return [...new Set(error.issues.map((issue) => String(issue.path[0])))];
}

/**
 * A deploy lacks bindings it cannot serve without. The message is built from
 * binding names alone, so an unhandled-error handler may log it as written,
 * where any other error's text could carry a secret.
 */
export class MissingBindingsError extends Error {
  override name = "MissingBindingsError";
  readonly missing: readonly string[];
  constructor(service: string, missing: readonly string[]) {
    super(`${service} is missing required binding(s): ${missing.join(", ")}`);
    this.missing = missing;
  }
}

export interface ResolveBindingsOptions {
  /** Opens the error message: `<service> is missing required binding(s): …`. */
  service: string;
  /** Absent bindings no string schema can hold, such as a Hyperdrive handle, named in the same message. */
  extraMissing?: readonly string[];
}

/**
 * The parsed bindings, or a {@link MissingBindingsError} naming every absent
 * one at once — a misconfigured deploy is fixed in one sitting, not one
 * redeploy per binding. The message says "missing", so the schema's fields
 * should validate presence only.
 */
export function resolveBindings<S extends z.ZodType>(
  schema: S,
  env: unknown,
  options: ResolveBindingsOptions,
): z.output<S> {
  const parsed = schema.safeParse(env);
  const missing = [
    ...(parsed.success ? [] : fieldsOf(parsed.error)),
    ...(options.extraMissing ?? []),
  ];
  if (!parsed.success || missing.length > 0) {
    throw new MissingBindingsError(options.service, missing);
  }
  return parsed.data;
}
